import { useEffect, useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { Button, Icon } from './primitives';

/**
 * The feedback components, at web sizes.
 *
 * All of these were written console-first in the handoff — they default to `size="tv"` and
 * reach for the `--fs-tv-*` scale, which README §4 says the web app never uses. The web
 * variants here are the same components at the web scale; nothing else about them changes.
 */

export type NoticeTone = 'error' | 'info' | 'warning';

const NOTICE: Record<NoticeTone, { glyph: 'error' | 'info' | 'warning'; color: string }> = {
  error: { glyph: 'error', color: 'var(--accent-danger)' },
  info: { glyph: 'info', color: 'var(--accent-calm)' },
  warning: { glyph: 'warning', color: 'var(--accent-warning)' },
};

export interface NoticeBannerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: NoticeTone;
  title?: ReactNode;
  /** Mono, for the machine half of the message: "HTTP 503 · GET /api/teams". */
  detail?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  children?: ReactNode;
}

/**
 * A warning above the content it is about — if teams could not be loaded, nothing below is
 * worth reading. The chrome stays: a page that fails must still let you switch team or sign
 * out, which is why this is a banner in the content region and not a whole-page error.
 */
export function NoticeBanner({
  tone = 'info',
  title,
  detail,
  actionLabel,
  onAction,
  className,
  children,
  ...rest
}: NoticeBannerProps) {
  const t = NOTICE[tone];
  return (
    <div className={['bf-notice', `is-${tone}`, className ?? ''].filter(Boolean).join(' ')} {...rest}>
      <Icon name={t.glyph} size={24} style={{ color: t.color }} />
      <div className="bf-notice-text">
        {title ? <span className="bf-notice-title">{title}</span> : null}
        {children ? <span className="bf-notice-body">{children}</span> : null}
        {detail ? <span className="bf-notice-detail">{detail}</span> : null}
      </div>
      {actionLabel ? (
        <Button variant="soft" size="md" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The seven install states.
 *
 * Both the chip label and the primary action that goes with a state are pure functions of the
 * state, so both are read from here — never hand-written at a call site. `ui/components/
 * Status.kt` holds the same seven on the Kotlin side; a state added to one belongs in both,
 * or the dashboard and the tester's Board start describing the same build differently.
 */
export type InstallState =
  | 'no_build' | 'not_installed' | 'installed' | 'up_to_date'
  | 'update_available' | 'newer_than_latest' | 'pinned';

export interface StatusParts {
  from?: number | string;
  to?: number | string;
  pinned?: number | string;
}

export const STATES: Record<InstallState, { label: (p: StatusParts) => string; tone: string; action: string | null }> = {
  no_build: { label: () => 'No build yet', tone: 'var(--ink-faint)', action: null },
  not_installed: { label: () => 'Not installed', tone: 'var(--ink-faint)', action: 'Install' },
  installed: { label: () => 'Installed', tone: 'var(--ink-muted)', action: 'Reinstall' },
  up_to_date: { label: () => 'Up to date', tone: 'var(--accent-success)', action: 'Open' },
  update_available: {
    label: (p) => `Update available (${p.from} → ${p.to})`,
    tone: 'var(--accent)', action: 'Update',
  },
  newer_than_latest: { label: () => 'Newer than latest', tone: 'var(--accent-warning)', action: 'Open' },
  pinned: {
    // No "v": `statusLabel` in `@boardflight/shared` names the version as it was published, and a
    // version called "v2" would otherwise read "vv2".
    label: (p) => `Pinned to ${p.pinned} · newer available`,
    tone: 'var(--accent-calm)', action: 'Open',
  },
};

export interface StatusChipProps extends HTMLAttributes<HTMLSpanElement>, StatusParts {
  state?: InstallState;
}

export function StatusChip({ state = 'not_installed', from, to, pinned, className, ...rest }: StatusChipProps) {
  const s = STATES[state] ?? STATES.not_installed;
  return (
    <span className={className ? `bf-chip ${className}` : 'bf-chip'} {...rest}>
      <span className="bf-chip-dot" style={{ background: s.tone }} />
      {s.label({ from, to, pinned })}
    </span>
  );
}

export interface RawMessageProps extends HTMLAttributes<HTMLDivElement> {
  label?: ReactNode;
  children?: ReactNode;
}

/**
 * Board's own text, verbatim.
 *
 * Never truncated, never summarised, never swapped for an icon or friendlier English.
 * `INSTALL_PARSE_FAILED_NO_CERTIFICATES: …` is the only thing that tells a developer why their
 * build was rejected, and BoardFlight never opens the artifact, so there is nothing else it could
 * say instead. Wraps rather than scrolls, and stays selectable so it can be pasted into a bug
 * report.
 *
 * **Where one of these is printed is a separate question from what it says**, and the two are easy
 * to run together. A build's own check prints its message in place: there is one of them and it is
 * the answer to the whole screen. The install feed does not — fifty rows each carrying five lines
 * of the same rejection is a page on which the one message that differs is invisible — so the row
 * gets a mark and the message opens in a `MessageDialog`, whole, verbatim and selectable, exactly
 * as it is here. Behind a control is a placement; a shorter version of the text is not.
 */
export function RawMessage({ label, className, children, ...rest }: RawMessageProps) {
  return (
    <div className={className ? `bf-raw ${className}` : 'bf-raw'} {...rest}>
      {label ? <span className="bf-raw-label">{label}</span> : null}
      <pre className="bf-raw-pre">{children}</pre>
    </div>
  );
}

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  value?: number;
  indeterminate?: boolean;
  tone?: 'brand' | 'success' | 'ink';
  label?: ReactNode;
}

export function ProgressBar({
  value = 0,
  indeterminate = false,
  tone = 'brand',
  label,
  className,
  ...rest
}: ProgressBarProps) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className={className ? `bf-progress ${className}` : 'bf-progress'} {...rest}>
      {label ? (
        <div className="bf-progress-label">
          <span>{label}</span>
          {!indeterminate ? <span>{Math.round(pct)}%</span> : null}
        </div>
      ) : null}
      <div
        className="bf-progress-track"
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`bf-progress-fill is-${tone}${indeterminate ? ' is-indeterminate' : ''}`}
          style={indeterminate ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * "Copied", "Saved" — an acknowledgement that goes away on its own.
 *
 * Deliberately not a toast system and not to become one: it belongs beside the control that
 * caused it, so the answer to "did that work?" is where you were already looking. `role=
 * "status"` because a copy button that silently succeeded is indistinguishable from one that
 * silently failed if you cannot see the page.
 */
export function TransientAck({ show, children = 'Saved' }: { show: boolean; children?: ReactNode }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!show) return setVisible(false);
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), 2400);
    return () => clearTimeout(timer);
  }, [show]);

  return (
    <span className={visible ? 'bf-ack is-shown' : 'bf-ack'} role="status">
      {visible ? (
        <>
          <Icon name="check" size={18} />
          {children}
        </>
      ) : null}
    </span>
  );
}
